import React, { ReactNode, useMemo } from 'react'
import { Box } from '@mui/material'
import {
  BarChart as BaseBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts'
import { chartWrapper, tooltipStyles, tooltipLabelStyles } from '../styles'

export type BarSeriesConfig = {
  key: string
  name: string
  color: string
}

type Orientation = 'vertical' | 'horizontal'

type StackedBarChartProps = {
  /** Final data. Each row already contains categoryKey, series keys, and (optionally) totalKey. */
  data: Array<Record<string, any>>
  /** Which field is the category axis (e.g. 'wheat_code') */
  categoryKey: string
  /** Series to stack */
  series: BarSeriesConfig[]
  /** Optional label text for axes (visual only) */
  xLabel?: string
  yLabel?: string
  /** If true, format the axis as percentages (expects values already 0–100). */
  percentage?: boolean
  /** Hide legend (default: false) */
  hideLegend?: boolean
  /** Bar orientation (default: 'vertical') */
  orientation?: Orientation
  /** If provided, replaces tooltip with a minimal tooltip showing only this key’s value. */
  tooltipKey?: string
  /** Optional renderer for tooltipKey’s value. Defaults to showing the raw value. */
  tooltipRender?: (value: any, row: any) => ReactNode
  /** Hide segment labels if below this threshold (default: 5). */
  minLabelValue?: number
  /** If provided, draw a top/right total label from this field. Parent must compute and include it in each row. */
  totalKey?: string
  /** Bar width/height (default: 64) */
  barSize?: number
  /** Hide tick labels on X axis (default: false) */
  hideXAxisLabels?: boolean
  /** Hide tick labels on Y axis (default: false) */
  hideYAxisLabels?: boolean
}

const DefaultTooltip = ({
  active,
  payload,
  label,
  percentage = false,
}: any) => {
  if (!active || !payload?.length) return null
  const cleanedPayload = payload
    .map((item) => ({
      ...item,
      name: item.name.replace(/^Wheat\s*/i, ''), // remove 'Wheat ' (case-insensitive)
    }))
    .reverse()

  const fmt = (n: number) => (percentage ? `${n}%` : n)

  return (
    <div style={{ ...tooltipStyles, padding: 8 }}>
      <div style={{ ...tooltipLabelStyles, fontWeight: 'bold' }}>{label}</div>
      {cleanedPayload.map((entry: any) => (
        <div key={entry.dataKey} style={tooltipLabelStyles}>
          {`${entry.name}: ${fmt(entry.value)}`}
        </div>
      ))}
    </div>
  )
}

export const StackedBarChart = ({
  data,
  categoryKey,
  series,
  xLabel,
  yLabel,
  percentage = false,
  hideLegend = false,
  orientation = 'vertical',
  tooltipKey,
  tooltipRender,
  minLabelValue = 5,
  totalKey,
  barSize = 40,
  hideXAxisLabels,
  hideYAxisLabels,
}: StackedBarChartProps) => {
  if (!data?.length) return null

  const isVertical = orientation === 'vertical'
  const domainMax = useMemo(() => {
    const totals = totalKey
      ? data.map((d) => Number(d[totalKey] ?? 0))
      : data.map((d) =>
          series.reduce((sum, s) => sum + Number(d[s.key] ?? 0), 0)
        )
    const max = Math.max(0, ...totals)
    return percentage ? 100 : Math.round(max * 1.33)
  }, [data, series, totalKey, percentage])

  const ScoreTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return null
    const row = payload[0].payload
    const val = row[tooltipKey!]
    return (
      <div style={{ ...tooltipStyles, padding: 8 }}>
        {tooltipRender ? tooltipRender(val, row) : <div>{val}</div>}
      </div>
    )
  }

  // Determine the last non-zero value for each segment to apply rounded corners
  const RoundedSegment = (props: any) => {
    const { x, y, width, height, fill, dataKey } = props
    const row = props.payload

    // find the last non-zero segment
    const topKey = [...series]
      .reverse()
      .find((s) => Number(row[s.key]) > 0)?.key
    const isTop = dataKey === topKey

    if (!isTop) {
      return <rect x={x} y={y} width={width} height={height} fill={fill} />
    }

    const r = 6

    // vertical = bars grow upward
    if (isVertical) {
      return (
        <path
          d={`
          M ${x},${y + r}
          Q ${x},${y} ${x + r},${y}
          L ${x + width - r},${y}
          Q ${x + width},${y} ${x + width},${y + r}
          L ${x + width},${y + height}
          L ${x},${y + height}
          Z
        `}
          fill={fill}
        />
      )
    }

    // horizontal = bars grow right
    return (
      <path
        d={`
        M ${x},${y}
        L ${x + width - r},${y}
        Q ${x + width},${y} ${x + width},${y + r}
        L ${x + width},${y + height - r}
        Q ${x + width},${y + height} ${x + width - r},${y + height}
        L ${x},${y + height}
        Z
      `}
        fill={fill}
      />
    )
  }

  return (
    <Box sx={chartWrapper}>
      <ResponsiveContainer width="100%" height={500}>
        <BaseBarChart
          data={data}
          layout={isVertical ? 'horizontal' : 'vertical'}
          margin={{ top: 20, right: 10, bottom: 0, left: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          {isVertical ? (
            <>
              <XAxis
                dataKey={categoryKey}
                type="category"
                tick={!hideXAxisLabels}
                label={
                  xLabel
                    ? { value: xLabel, position: 'insideBottom', offset: -10 }
                    : undefined
                }
              />
              <YAxis
                type="number"
                domain={[0, domainMax]}
                tick={!hideYAxisLabels}
                tickFormatter={
                  percentage ? (v) => `${Math.round(v)}%` : undefined
                }
                label={
                  yLabel
                    ? { value: yLabel, angle: -90, position: 'insideLeft' }
                    : undefined
                }
              />
            </>
          ) : (
            <>
              <XAxis
                type="number"
                tick={!hideXAxisLabels}
                domain={[0, domainMax]}
                tickFormatter={percentage ? (v) => `${v}%` : undefined}
                label={
                  xLabel
                    ? { value: xLabel, position: 'insideBottom', offset: -10 }
                    : undefined
                }
              />
              <YAxis
                dataKey={categoryKey}
                type="category"
                tick={!hideYAxisLabels}
                label={
                  yLabel
                    ? { value: yLabel, angle: -90, position: 'insideLeft' }
                    : undefined
                }
              />
            </>
          )}

          <Tooltip
            cursor={{ fill: '#f8f8f8' }}
            content={
              tooltipKey ? (
                <ScoreTooltip />
              ) : (
                (props) => <DefaultTooltip {...props} percentage={percentage} />
              )
            }
          />
          {!hideLegend && (
            <Legend
              formatter={(value: string) => value.replace(/^Wheat\s*/i, '')}
            />
          )}

          {series.map((s, idx) => {
            const isTopSegment = idx === series.length - 1

            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId="stack"
                fill={s.color}
                barSize={barSize}
                shape={RoundedSegment}
              >
                {/* Inner segment labels, hidden under threshold */}
                <LabelList
                  dataKey={s.key}
                  position="center"
                  fill="black"
                  formatter={(v: any) => {
                    const val = Math.round(Number(v))
                    if (val < minLabelValue) return ''
                    return percentage ? `${val}%` : String(val)
                  }}
                />
                {/* Total label sits on the top segment so it's perfectly aligned */}
                {isTopSegment && totalKey && (
                  <LabelList
                    position={isVertical ? 'top' : 'right'}
                    valueAccessor={(_, index) => data[index][totalKey]}
                    formatter={(v: any) => (v == null ? '' : String(v))}
                  />
                )}
              </Bar>
            )
          })}
        </BaseBarChart>
      </ResponsiveContainer>
    </Box>
  )
}
